/**
 * Maxio "Future State Systems Landscape": the same hub-and-spoke diagram as Rillet, with Maxio at
 * the centre, DERIVED from the Maxio questionnaire answers (no separate tech-stack editor).
 *
 * Layout follows the Maxio partner team's "How Maxio Works" slide (NetSuite + Maxio deck): CRM on
 * the left, payment processor on the right, ERP / GL below, "your application / website" above.
 * Links are two-way like the deck, except a disconnected GL (journal export, Maxio -> GL only).
 * The client's usage event feed is not drawn (Oct 20). The current billing system is drawn as Sunset (red, no link):
 * its data is migrated, then it is retired.
 *
 * Pure and dependency-light: the web deal page and the server SOW renderer both call it, so the
 * picture on screen and the one in the Word export are identical.
 */
import { buildMaxioFlowLandscape } from './maxio-flow-layout.js';
export const MAXIO_LANDSCAPE_HUB_NAME = 'Maxio';
/** Category slots for the Maxio diagram (see the module header). */
export const MAXIO_LANDSCAPE_PLACEMENT = {
    left: ['crm', 'cpq', 'billing-engines'],
    above: ['maxio-product'],
    below: ['erp', 'taxes'],
    right: ['payments', 'custom-integration'],
};
export const MAXIO_LANDSCAPE_CATEGORY_LABELS = {
    crm: 'CRM',
    cpq: 'CPQ',
    'billing-engines': 'Current Billing',
    'maxio-product': 'Your Product',
    erp: 'General Ledger / ERP',
    taxes: 'Sales Tax',
    payments: 'Payment Processor',
    'custom-integration': 'Custom Integration',
};
/** Options to pass to `buildTechStackLandscape` for a Maxio deal. */
export const MAXIO_LANDSCAPE_OPTIONS = {
    hubName: MAXIO_LANDSCAPE_HUB_NAME,
    placement: MAXIO_LANDSCAPE_PLACEMENT,
    categoryLabels: MAXIO_LANDSCAPE_CATEGORY_LABELS,
};
/** Display names for Maxio option values, used when the caller has no option labels. */
const MAXIO_OPTION_NAMES = {
    gl_target: {
        qbo: 'QuickBooks Online',
        ies: 'Intuit Enterprise Suite',
        xero: 'Xero',
        netsuite: 'NetSuite',
        sage_intacct: 'Sage Intacct',
        rillet: 'Rillet',
    },
    crm_system: {
        salesforce: 'Salesforce',
        hubspot: 'HubSpot',
        pipedrive: 'Pipedrive',
        other: 'Other CRM',
    },
    tax_connector: {
        avalara: 'Avalara',
        anrok: 'Anrok',
        taxjar: 'TaxJar',
        other: 'Other tax engine',
    },
    current_billing_system: {
        stripe_billing: 'Stripe Billing',
        chargebee: 'Chargebee',
        zuora: 'Zuora',
        recurly: 'Recurly',
        spreadsheets: 'Spreadsheets',
        homegrown: 'Homegrown system',
        other: 'Other billing system',
    },
    previous_erp: {
        qbo: 'QuickBooks Online',
        ies: 'Intuit Enterprise Suite',
        xero: 'Xero',
        netsuite: 'NetSuite',
        sage_intacct: 'Sage Intacct',
        rillet: 'Rillet',
        qb_desktop: 'QuickBooks Desktop',
        other: 'Previous ERP',
    },
};
/** True for the Maxio product key, bare or composite ('maxio' / 'maxio:maxio'). */
export function isMaxioProductKey(productKey) {
    if (!productKey)
        return false;
    const k = productKey.toLowerCase();
    return k === 'maxio' || k === 'maxio:maxio';
}
function list(value) {
    if (Array.isArray(value)) {
        return value
            .map((v) => (typeof v === 'object' && v !== null ? v.value : v))
            .filter((v) => typeof v === 'string' || typeof v === 'number')
            .map((v) => String(v).trim())
            .filter(Boolean);
    }
    if (typeof value === 'string') {
        const t = value.trim();
        if (!t)
            return [];
        if (t.startsWith('[')) {
            try {
                return list(JSON.parse(t));
            }
            catch {
                /* plain string */
            }
        }
        return t
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
    }
    if (typeof value === 'number' && Number.isFinite(value))
        return [String(value)];
    return [];
}
function system(id, category, name, direction, plan = 'keep', flowTarget, logoKey) {
    const inScope = direction !== null;
    return {
        id,
        category,
        name,
        plan,
        inScope,
        integration: inScope
            ? { type: 'standard', hours: 0, direction, ...(flowTarget ? { flowTarget } : {}) }
            : undefined,
        ...(logoKey ? { logoKey } : {}),
    };
}
/** Logo key for an option value ('stripe_billing' -> 'stripe'); other values map to themselves. */
function logoKeyFor(value) {
    if (!value || value === 'other' || value.startsWith('other_'))
        return undefined;
    if (value === 'stripe_billing')
        return 'stripe';
    if (value === 'maxio_payments')
        return 'maxio';
    return value;
}
const GATEWAY_NAMES = {
    stripe: 'Stripe',
    braintree: 'Braintree',
    adyen: 'Adyen',
    authorize_net: 'Authorize.net',
    paypal: 'PayPal',
};
/** Build the diagram's systems from a Maxio deal's answers. */
export function buildMaxioLandscapeSystems(source) {
    const first = (k) => list(source.getRawValue(k))[0];
    const label = (k, v) => source.getOptionLabel(k, v) ?? MAXIO_OPTION_NAMES[k]?.[v] ?? v;
    /** Free text typed into an "Other" follow-up field, if any. */
    const typed = (k) => {
        const v = source.getRawValue(k);
        return typeof v === 'string' && v.trim() ? v.trim() : undefined;
    };
    const out = [];
    // Left: CRM (+ CPQ), and the billing system being retired
    const crm = first('crm_system');
    const hasCrm = !!crm && crm !== 'none';
    if (hasCrm) {
        const name = (crm === 'other' ? typed('crm_system_other') : undefined) ?? label('crm_system', crm);
        out.push(system('maxio-crm', 'crm', name, 'two_way', 'keep', undefined, logoKeyFor(crm)));
    }
    const cpq = first('cpq_scope');
    if (cpq === 'maxio_cpq' || list(source.getRawValue('maxio_add_ons')).includes('cpq')) {
        // Maxio CPQ runs inside the CRM ("CPQ in the CRM") when there is one.
        out.push(system('maxio-cpq', 'cpq', 'Maxio CPQ', 'two_way', 'keep', hasCrm ? 'maxio-crm' : undefined, 'maxio'));
    }
    else if (cpq === 'external_cpq') {
        out.push(system('maxio-cpq', 'cpq', typed('external_cpq_tool') ?? 'Existing CPQ tool', 'two_way'));
    }
    const current = first('current_billing_system');
    if (current && current !== 'maxio') {
        out.push(system('maxio-current-billing', 'billing-engines', (current === 'other' ? typed('current_billing_system_other') : undefined) ??
            label('current_billing_system', current), null, 'sunset', undefined, logoKeyFor(current)));
    }
    // Above: the client's website (hosted pages). No "Product usage events" box (Akash, Oct 20):
    // the usage feed is the client's own engineering work, not a system in Maxio's standard
    // architecture, so it is never drawn - usage revenue shows in the SOW scope, not the diagram.
    const hosted = first('hosted_page_customization');
    if (list(source.getRawValue('self_serve_features')).length > 0 ||
        (hosted && hosted !== 'smart_templates')) {
        out.push(system('maxio-product-web', 'maxio-product', 'Website / hosted pages', 'two_way'));
    }
    // Below: general ledger and tax engine
    const gl = first('gl_target');
    if (gl) {
        const other = typed('gl_target_other');
        const name = gl === 'other_disconnected'
            ? `${other ?? 'Other GL'} (journal export)`
            : gl === 'other_unsupported'
                ? `${other ?? 'Other GL'} (custom integration)`
                : label('gl_target', gl);
        out.push(system('maxio-gl', 'erp', name, gl === 'other_disconnected' ? 'outbound' : 'two_way', 'keep', undefined, logoKeyFor(gl)));
    }
    // ERP changing during the project: the OLD ERP is retired (Sunset, no link). The general ledger
    // above is the new system Maxio connects to and stays Keep.
    const previous = first('previous_erp');
    if (first('erp_changing') === 'yes' && previous && previous !== gl) {
        const name = (previous === 'other' ? typed('previous_erp_other') : undefined) ??
            label('previous_erp', previous);
        out.push(system('maxio-old-erp', 'erp', name, null, 'sunset', undefined, logoKeyFor(previous)));
    }
    const tax = first('tax_connector');
    if (tax && !['none', 'manual'].includes(tax)) {
        const name = (tax === 'other' ? typed('tax_connector_other') : undefined) ?? label('tax_connector', tax);
        out.push(system('maxio-tax', 'taxes', name, 'two_way', 'keep', undefined, logoKeyFor(tax)));
    }
    // Payment processors (one box per named gateway) and the gateway being replaced
    const processor = first('payment_processor_plan');
    const gateways = Number(first('payment_gateway_count'));
    const switching = first('gateway_switch') === 'yes';
    const named = list(source.getRawValue('payment_gateways'));
    const answeredPayments = !!processor ||
        switching ||
        named.length > 0 ||
        (Number.isFinite(gateways) && gateways > 0) ||
        list(source.getRawValue('payment_methods')).length > 0;
    if (answeredPayments) {
        if (switching) {
            const prev = first('previous_gateway');
            out.push(system('maxio-payments-current', 'payments', (prev && GATEWAY_NAMES[prev]) ?? 'Current gateway', null, 'sunset', undefined, logoKeyFor(prev)));
        }
        if (processor === 'maxio_payments') {
            out.push(system('maxio-payments', 'payments', 'Maxio Payments', 'two_way', 'keep', undefined, 'maxio'));
        }
        else if (processor === 'unsupported') {
            out.push(system('maxio-payments', 'payments', typed('unsupported_processor_name') ?? 'Unsupported processor (custom)', 'two_way'));
        }
        else if (named.length > 0) {
            for (const g of named) {
                const name = g === 'other'
                    ? (typed('payment_gateways_other') ?? 'Other gateway')
                    : (GATEWAY_NAMES[g] ?? g);
                out.push(system(`maxio-payments-${g}`, 'payments', name, 'two_way', 'keep', undefined, logoKeyFor(g)));
            }
        }
        else {
            const payName = Number.isFinite(gateways) && gateways > 1
                ? `${gateways} payment gateways`
                : 'Payment gateway';
            out.push(system('maxio-payments', 'payments', payName, 'two_way'));
        }
    }
    // Custom integrations: one box per named system, else a count
    const flows = Number(first('custom_integration_flows'));
    const customNames = (typed('custom_integration_systems') ?? '')
        .split(',')
        .map((n) => n.trim())
        .filter(Boolean);
    if (customNames.length > 0) {
        customNames.forEach((n, i) => out.push(system(`maxio-custom-${i + 1}`, 'custom-integration', n, 'two_way')));
    }
    else if (Number.isFinite(flows) && flows > 0) {
        out.push(system('maxio-custom', 'custom-integration', flows === 1 ? '1 custom integration' : `${flows} custom integrations`, 'two_way'));
    }
    return out;
}
const MAXIO_PRODUCT_LINES = {
    core: 'Maxio Core',
    advanced_billing: 'Advanced Billing',
    both: 'Core + Advanced Billing',
};
/** Add-ons that run inside the platform (CPQ is drawn with the CRM, not as a chip). */
const MAXIO_ADD_ON_CHIPS = {
    advanced_ar: 'Advanced AR',
    advanced_revenue: 'Advanced Revenue',
    projects: 'Projects',
    expenses: 'Expenses',
};
/**
 * The full Maxio diagram from a deal's answers: systems (`buildMaxioLandscapeSystems`) laid out left
 * to right (`buildMaxioFlowLandscape`), with the product line and add-on chips on the Maxio card.
 * The web deal page and the server SOW renderer both call this, so both draw the same picture.
 */
export function buildMaxioLandscape(source, logoFor) {
    const systems = buildMaxioLandscapeSystems(source);
    const product = list(source.getRawValue('maxio_products'))[0];
    const addOns = new Set(list(source.getRawValue('maxio_add_ons')).filter((a) => a in MAXIO_ADD_ON_CHIPS));
    if (list(source.getRawValue('services_billing'))[0] === 'projects_in_maxio')
        addOns.add('projects');
    return buildMaxioFlowLandscape(systems, {
        hubName: MAXIO_LANDSCAPE_HUB_NAME,
        product: product ? (MAXIO_PRODUCT_LINES[product] ?? null) : null,
        addOns: Object.keys(MAXIO_ADD_ON_CHIPS)
            .filter((k) => addOns.has(k))
            .map((k) => MAXIO_ADD_ON_CHIPS[k]),
        categoryLabels: MAXIO_LANDSCAPE_CATEGORY_LABELS,
        ...(logoFor ? { logoFor } : {}),
    });
}
//# sourceMappingURL=maxio-landscape.js.map